import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface Solution {
  id: number;
  title: string;
  description: string;
  icon: JSX.Element;
  benefits: string[];
  color: string;
}

const Solutions: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [activeSolution, setActiveSolution] = useState<number>(1);

  const solutions: Solution[] = [
    {
      id: 1,
      title: "Customer Service Agents",
      description: "AI-powered agents that handle customer inquiries 24/7, providing instant responses and resolving issues efficiently.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
        </svg>
      ),
      benefits: [
        "24/7 availability for global customer support",
        "Instant response to common inquiries",
        "Seamless escalation to human agents when needed",
        "Continuous learning from customer interactions"
      ],
      color: "from-electric-blue to-teal-accent"
    },
    {
      id: 2,
      title: "Data Processing Agents",
      description: "Intelligent agents that automate data extraction, transformation, and analysis, turning raw data into actionable insights.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      benefits: [
        "Automated data extraction from various sources",
        "Advanced pattern recognition and anomaly detection",
        "Real-time data processing and analysis",
        "Custom reporting and visualization"
      ],
      color: "from-neon-blue to-electric-blue"
    },
    {
      id: 3,
      title: "Workflow Automation Agents",
      description: "Agents that streamline business processes by automating repetitive tasks, coordinating workflows, and ensuring timely execution.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
        </svg>
      ),
      benefits: [
        "Automated task assignment and tracking",
        "Intelligent process routing and prioritization",
        "Integration with existing business systems",
        "Reduction in manual errors and processing time"
      ],
      color: "from-teal-accent to-neon-blue"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    },
  };

  return (
    <section id="solutions" className="section-padding bg-deep-blue relative">
      {/* Background decorative elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      <div className="absolute left-0 top-1/3 w-64 h-64 bg-electric-blue/10 rounded-full blur-3xl"></div>
      <div className="absolute right-0 bottom-1/4 w-80 h-80 bg-teal-accent/10 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl font-bold mb-6">
            Our AI <span className="gradient-text">Solutions</span>
          </motion.h2>
          
          <motion.p variants={itemVariants} className="text-lg max-w-3xl mx-auto text-gray-300">
            Our intelligent AI agents are designed to automate complex workflows, enhance productivity, 
            and deliver exceptional results across various business functions.
          </motion.p>
        </motion.div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {solutions.map((solution) => (
            <motion.div
              key={solution.id}
              variants={itemVariants}
              className={`bg-dark-blue/80 p-6 rounded-lg border border-electric-blue/20 cursor-pointer transition-all ${
                activeSolution === solution.id ? 'shadow-neon scale-105' : 'hover:shadow-neon hover:scale-105'
              }`}
              onClick={() => setActiveSolution(solution.id)}
            >
              <div className={`w-16 h-16 bg-gradient-to-br ${solution.color} rounded-full flex items-center justify-center mb-4`}>
                {solution.icon}
              </div>
              <h3 className="text-xl font-bold mb-2">{solution.title}</h3>
              <p className="text-gray-400 mb-4">{solution.description}</p>
              <div className={`h-1 w-20 bg-gradient-to-r ${solution.color} rounded-full ${
                activeSolution === solution.id ? 'opacity-100' : 'opacity-50'
              }`}></div>
            </motion.div>
          ))}
        </div>
        
        {/* Solution details */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="bg-dark-blue/50 p-8 rounded-xl border border-electric-blue/30 shadow-neon"
        >
          {solutions.map((solution) => (
            activeSolution === solution.id && (
              <motion.div
                key={`detail-${solution.id}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
              >
                <div>
                  <h3 className="text-2xl font-bold mb-4 gradient-text">{solution.title}</h3>
                  <p className="text-gray-300 mb-6">{solution.description}</p>
                  
                  <h4 className="text-xl font-semibold mb-4">Key Benefits:</h4>
                  <ul className="space-y-3">
                    {solution.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start">
                        <span className={`inline-block w-6 h-6 mr-2 bg-gradient-to-br ${solution.color} rounded-full flex-shrink-0 flex items-center justify-center text-deep-blue font-bold`}>
                          ✓
                        </span>
                        <span className="text-gray-300">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="relative">
                  {/* Interactive visualization */}
                  <div className="aspect-w-16 aspect-h-9 bg-deep-blue rounded-lg overflow-hidden border border-electric-blue/30 shadow-neon">
                    <div className="absolute inset-0 flex items-center justify-center">
                      {/* Process flow visualization */}
                      <div className="w-full max-w-md">
                        <div className="relative py-12">
                          {/* Input */}
                          <div className="absolute left-0 top-0 w-24 h-24 bg-dark-blue rounded-lg border border-electric-blue/50 flex items-center justify-center">
                            <span className="text-sm text-gray-300">Input Data</span>
                          </div>
                          
                          {/* Processing */}
                          <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-gradient-to-br from-electric-blue/20 to-teal-accent/20 rounded-full border border-electric-blue/50 flex items-center justify-center animate-pulse-slow">
                            <div className="text-center">
                              <div className={`w-12 h-12 mx-auto bg-gradient-to-br ${solution.color} rounded-full flex items-center justify-center mb-2`}>
                                {solution.icon}
                              </div>
                              <span className="text-sm text-white">AI Processing</span>
                            </div>
                          </div>
                          
                          {/* Output */}
                          <div className="absolute right-0 bottom-0 w-24 h-24 bg-dark-blue rounded-lg border border-electric-blue/50 flex items-center justify-center">
                            <span className="text-sm text-gray-300">Results</span>
                          </div>
                          
                          {/* Connection lines */}
                          <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
                            <defs>
                              <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="#00FFFF" />
                                <stop offset="100%" stopColor="#00CED1" />
                              </linearGradient>
                            </defs>
                            <path d="M24 24 L 160 80" stroke="url(#lineGradient)" strokeWidth="2" fill="none" strokeDasharray="6 4" />
                            <path d="M224 80 L 360 136" stroke="url(#lineGradient)" strokeWidth="2" fill="none" strokeDasharray="6 4" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Solutions;